import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { IBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	FastshiftRunbookStepStatus,
	OrgQueryParam,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { CustomerDomainOrg, Org, PageStateCache, PortalUserConsent, TableId } from './types';
import { filterForActiveOrgs } from './utils';

export const Actions = {
	GET_USER_DETAILS: '[FASTSHIFT] GET_USER_DETAILS',
	GET_USER_ACCESS: '[FASTSHIFT] GET_USER_ACCESS',
	GET_CUSTOMER_DOMAINS: '[FASTSHIFT] GET_CUSTOMER_DOMAINS',
	SET_SELECTED_ORG: '[FASTSHIFT] SET_SELECTED_ORG',
	SET_SELECTED_DOMAIN_ORG: '[FASTSHIFT] SET_SELECTED_DOMAIN_ORG',
	GET_ACCOUNT_ORGS: '[FASTSHIFT] GET_ACCOUNT_ORGS',
	GET_MIGRATION_PLANS: '[FASTSHIFT] GET_MIGRATION_PLANS',
	GET_MIGRATION_PLAN_TOTALS: '[FASTSHIFT] GET_MIGRATION_PLAN_TOTALS',
	GET_L1_ASSESSMENTS: '[FASTSHIFT] GET_L1_ASSESSMENTS',
	GET_INSTANCES: '[FASTSHIFT] GET_INSTANCES',
	GET_CUSTOMER_DOMAIN: '[FASTSHIFT] GET_CUSTOMER_DOMAIN',
	GET_CUSTOMER_DOMAIN_ENGAGEMENT_DETAILS: '[FASTSHIFT] GET_CUSTOMER_DOMAIN_ENGAGEMENT_DETAILS',
	CLEAR_ALL_CUSTOMER_DOMAIN_SCOPED_STATE: '[FASTSHIFT] CLEAR_ALL_CUSTOMER_DOMAIN_SCOPED_STATE',
	CLEAR_CUSTOMER_DOMAIN: '[FASTSHIFT] CLEAR_CUSTOMER_DOMAIN',
	GET_USER_TYPE: '[FASTSHIFT] GET_USER_TYPE',
	GET_FASTSHIFT_ORGANIZATIONS: '[FASTSHIFT] GET_FASTSHIFT_ORGANIZATIONS',
	GET_FASTSHIFT_ORGANIZATION_SUMMARY: '[FASTSHIFT] GET_FASTSHIFT_ORGANIZATION_SUMMARY',
	GET_FASTSHIFT_ORGANIZATION_DC_INSTANCES: '[FASTSHIFT] GET_FASTSHIFT_ORGANIZATION_DC_INSTANCES',
	GET_FASTSHIFT_DC_INSTANCE_DETAILS: '[FASTSHIFT] GET_FASTSHIFT_DC_INSTANCE_DETAILS',
	GET_FASTSHIFT_SUPPORT_REQUESTS: '[FASTSHIFT] GET_FASTSHIFT_SUPPORT_REQUESTS',
	SET_TABLE_CONTROLS: '[FASTSHIFT] SET_TABLE_CONTROLS',
	CLEAR_TABLE_CONTROLS: '[FASTSHIFT] CLEAR_TABLE_CONTROLS',
	GET_FASTSHIFT_TOP_SUPPORT_REQUESTS: '[FASTSHIFT] GET_FASTSHIFT_TOP_SUPPORT_REQUESTS',
	SET_PORTAL_USER_CONSENT: '[FASTSHIFT] SET_PORTAL_USER_CONSENT',
	SUBMIT_PORTAL_USER_CONSENT: '[FASTSHIFT] SUBMIT_PORTAL_USER_CONSENT',
	GET_PORTAL_USER_CONSENT: '[FASTSHIFT] GET_PORTAL_USER_CONSENT',
	GET_FASTSHIFT_RUNBOOKS_BY_SUPPORT_TICKET_ID:
		'[FASTSHIFT] GET_FASTSHIFT_RUNBOOKS_BY_SUPPORT_TICKET_ID',
	GET_FASTSHIFT_RUNBOOK: '[FASTSHIFT] GET_FASTSHIFT_RUNBOOK',
	UPDATE_FASTSHIFT_RUNBOOK_STEP_STATUS: '[FASTSHIFT] UPDATE_FASTSHIFT_RUNBOOK_STEP_STATUS',
	UPDATE_FASTSHIFT_RUNBOOK_STEP_RISK: '[FASTSHIFT] UPDATE_FASTSHIFT_RUNBOOK_STEP_RISK',
	UPDATE_FASTSHIFT_RUNBOOK_STEP_DISABLED: '[FASTSHIFT] UPDATE_FASTSHIFT_RUNBOOK_STEP_DISABLED',
} as const;

export const setSelectedOrg = createAction<Org>(Actions.SET_SELECTED_ORG);

export const setSelectedDomainOrg = createAction<CustomerDomainOrg>(
	Actions.SET_SELECTED_DOMAIN_ORG,
);

export const getAccountOrgs = createAsyncThunk(
	Actions.GET_ACCOUNT_ORGS,
	async (stargateClient: IBxpWebStargateClient) => {
		const response = await stargateClient.getAccountOrgs({});

		const responseAdminhub = await stargateClient.getAdminhubAccountOrgs({});

		return filterForActiveOrgs(response?.accountOrgs, responseAdminhub?.accountOrgs);
	},
);

export const getMigrationPlanTotals = createAsyncThunk(
	Actions.GET_MIGRATION_PLAN_TOTALS,
	async ({ stargateClient, orgId }: { stargateClient: IBxpWebStargateClient; orgId: string }) => {
		const response = await stargateClient.getMigrationPlanTotalsProxy({
			orgId,
		});
		return response.data;
	},
);

export const getL1Assessments = createAsyncThunk(
	Actions.GET_L1_ASSESSMENTS,
	async ({ stargateClient, orgId }: { stargateClient: IBxpWebStargateClient; orgId: string }) => {
		const response = await stargateClient.getL1AssessmentsProxy({
			orgId,
		});
		return response.data;
	},
);

export const getInstances = createAsyncThunk(
	Actions.GET_INSTANCES,
	async ({ stargateClient, orgId }: { stargateClient: IBxpWebStargateClient; orgId: string }) => {
		const response = await stargateClient.getInstancesProxy({
			orgId,
		});
		return response.data;
	},
);

export const getCustomerDomains = createAsyncThunk(
	Actions.GET_CUSTOMER_DOMAINS,
	async ({
		stargateClient,
		controls,
	}: {
		stargateClient: IBxpWebStargateClient;
		controls: {
			page: number;
			size: number;
			sortBy?: string;
			sortDirection?: 'ASC' | 'DESC';
			searchTerm?: string;
			filters: Record<string, string | null>;
		};
	}) => {
		const response = await stargateClient.getFastshiftCustomerDomains({
			page: controls.page,
			size: controls.size,
			sortBy: controls.sortBy,
			sortDirection: controls.sortDirection,
			searchTerm: controls.searchTerm,
			filters: controls.filters,
		});
		return response;
	},
);

export const getCustomerDomain = createAsyncThunk(
	Actions.GET_CUSTOMER_DOMAIN,
	async ({
		stargateClient,
		customerDomainId,
	}: {
		stargateClient: IBxpWebStargateClient;
		customerDomainId: string;
	}) => {
		const response = await stargateClient.getFastshiftCustomerDomain({
			customerDomainId,
		});
		return response.data;
	},
);

export const getCustomerDomainEngagementDetails = createAsyncThunk(
	Actions.GET_CUSTOMER_DOMAIN_ENGAGEMENT_DETAILS,
	async ({
		stargateClient,
		customerDomainId,
	}: {
		stargateClient: IBxpWebStargateClient;
		customerDomainId: string;
	}) => {
		const response = await stargateClient.getFastshiftCustomerDomainEngagement({
			customerDomainId,
		});
		return response.data;
	},
);

export const clearAllCustomerDomainScopedState = createAction(
	Actions.CLEAR_ALL_CUSTOMER_DOMAIN_SCOPED_STATE,
);

export const clearCustomerDomain = createAction(Actions.CLEAR_CUSTOMER_DOMAIN);

export const getUserAccess = createAsyncThunk(
	Actions.GET_USER_ACCESS,
	async (stargateClient: IBxpWebStargateClient) => {
		const response = await stargateClient.getFastshiftUserAccess({});
		return response.data;
	},
);

export const getFastshiftTopSupportRequests = createAsyncThunk(
	Actions.GET_FASTSHIFT_TOP_SUPPORT_REQUESTS,
	async ({
		stargateClient,
		domainId,
		count,
		queryParams,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
		count: number;
		queryParams: OrgQueryParam;
	}) => {
		const response = await stargateClient.getFastshiftTopSupportRequests({
			domainId,
			count,
			queryParams,
		});
		return response.data;
	},
);

export const getFastshiftOrganizations = createAsyncThunk(
	Actions.GET_FASTSHIFT_ORGANIZATIONS,
	async ({
		stargateClient,
		domainId,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
	}) => {
		const response = await stargateClient.getFastshiftOrganizations({ domainId });

		return response;
	},
);

export const getFastshiftOrganizationSummary = createAsyncThunk(
	Actions.GET_FASTSHIFT_ORGANIZATION_SUMMARY,
	async ({
		stargateClient,
		domainId,
		orgId,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
		orgId: string;
	}) => {
		const response = await stargateClient.getFastshiftOrganizationSummary({ domainId, orgId });

		return response;
	},
);

export const getFastshiftOrganizationDcInstances = createAsyncThunk(
	Actions.GET_FASTSHIFT_ORGANIZATION_DC_INSTANCES,
	async ({
		stargateClient,
		domainId,
		orgId,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
		orgId: string;
	}) => {
		return stargateClient.getFastshiftOrganizationDcInstances({ domainId, orgId });
	},
);

export const getFastshiftDcInstanceDetails = createAsyncThunk(
	Actions.GET_FASTSHIFT_DC_INSTANCE_DETAILS,
	async ({
		stargateClient,
		domainId,
		orgId,
		instanceId,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
		orgId: string;
		instanceId: string;
	}) => {
		return stargateClient.getFastshiftDcInstanceDetails({ domainId, orgId, instanceId });
	},
);

export const getFastshiftSupportRequests = createAsyncThunk(
	Actions.GET_FASTSHIFT_SUPPORT_REQUESTS,
	async ({
		stargateClient,
		customerDomainId,
		queryParams,
	}: {
		stargateClient: IBxpWebStargateClient;
		customerDomainId: string;
		queryParams: OrgQueryParam;
	}) => {
		const response = await stargateClient.getFastshiftSupportRequests({
			customerDomainId,
			queryParams,
		});

		return response;
	},
);

export const getPortalUserConsent = createAsyncThunk(
	Actions.GET_PORTAL_USER_CONSENT,
	async ({
		stargateClient,
		displayedText,
	}: {
		stargateClient: IBxpWebStargateClient;
		displayedText: string;
	}) => {
		const response = await stargateClient.fetchFastshiftPortalConsentRequest({
			requestBody: {
				displayedText,
			},
		});
		return response.data.consent;
	},
);

export const submitPortalUserConsent = createAsyncThunk(
	Actions.SUBMIT_PORTAL_USER_CONSENT,
	async ({
		stargateClient,
		displayedText,
		locale,
	}: {
		stargateClient: IBxpWebStargateClient;
		displayedText: string;
		locale: string;
	}) => {
		const response = await stargateClient.sendFastshiftPortalConsent({
			requestBody: {
				displayedText,
				locale,
			},
		});
		return response.data.consent;
	},
);

export const setPortalUserConsent = createAction<PortalUserConsent['data']>(
	Actions.SET_PORTAL_USER_CONSENT,
);

export const setTableControls = createAction<{
	tableId: TableId;
	controls: PageStateCache[TableId];
}>(Actions.SET_TABLE_CONTROLS);

export const clearTableControls = createAction<{
	tableId: TableId;
}>(Actions.CLEAR_TABLE_CONTROLS);

export const getFastshiftRunbooksBySupportTicketId = createAsyncThunk(
	Actions.GET_FASTSHIFT_RUNBOOKS_BY_SUPPORT_TICKET_ID,
	async ({
		stargateClient,
		supportTicketId,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
	}) => {
		const response = await stargateClient.getFastshiftRunbooksBySupportTicketId({
			supportTicketId,
		});

		return response.data;
	},
);

export const getFastshiftRunbook = createAsyncThunk(
	Actions.GET_FASTSHIFT_RUNBOOK,
	async ({
		stargateClient,
		supportTicketId,
		runbookId,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
		runbookId: string;
	}) => {
		const response = await stargateClient.getFastshiftRunbook({
			supportTicketId,
			runbookId,
		});

		return response.data;
	},
);

export const updateFastshiftRunbookStepStatus = createAsyncThunk(
	Actions.UPDATE_FASTSHIFT_RUNBOOK_STEP_STATUS,
	async ({
		stargateClient,
		supportTicketId,
		runbookId,
		stepId,
		status,
		note,
		title,
		phase,
		responsible,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
		runbookId: string;
		stepId: string;
		status: FastshiftRunbookStepStatus;
		note?: string;
		title: string;
		phase: string;
		responsible: string;
	}) => {
		const response = await stargateClient.updateFastshiftRunbookStepStatus({
			supportTicketId,
			runbookId,
			stepId,
			status,
			note,
			title,
			phase,
			responsible,
		});

		return response;
	},
);

export const updateFastshiftRunbookStepRisk = createAsyncThunk(
	Actions.UPDATE_FASTSHIFT_RUNBOOK_STEP_RISK,
	async ({
		stargateClient,
		supportTicketId,
		runbookId,
		stepId,
		risk,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
		runbookId: string;
		stepId: string;
		risk: boolean;
	}) => {
		const response = await stargateClient.updateFastshiftRunbookStepRisk({
			supportTicketId,
			runbookId,
			stepId,
			risk,
		});

		return response;
	},
);

export const updateFastshiftRunbookStepDisabled = createAsyncThunk(
	Actions.UPDATE_FASTSHIFT_RUNBOOK_STEP_DISABLED,
	async ({
		stargateClient,
		supportTicketId,
		runbookId,
		stepId,
		disabled,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
		runbookId: string;
		stepId: string;
		disabled: boolean;
	}) => {
		const response = await stargateClient.updateFastshiftRunbookStepDisabled({
			supportTicketId,
			runbookId,
			stepId,
			disabled,
		});

		return response;
	},
);

export type Actions = (typeof Actions)[keyof typeof Actions];
